import Pusher from 'pusher-js/with-encryption';

/**
 * One Pusher socket per tab, opened on the first channel subscription and
 * closed once the last one is released. Pusher bills by concurrent
 * connection, so nothing here connects on page load or keeps a socket alive
 * for a feature that is not on screen.
 *
 * Channels are reference counted: two components on the same channel share
 * the subscription, and each binds and unbinds only its own callbacks.
 */

interface PusherEnvVars {
  REACT_APP_PUSHER_APP_KEY: string;
  REACT_APP_PUSHER_CLUSTER: string;
}

export type PusherEventHandlers = Record<string, (data: never) => void>;

// Route transitions unmount and remount subscribers within a frame; holding
// the socket briefly avoids a reconnect handshake on every navigation.
const DISCONNECT_GRACE_MS = 5000;

let client: Pusher | null = null;
let clientKey = '';
const channelRefCounts = new Map<string, number>();
let disconnectTimer: ReturnType<typeof setTimeout> | null = null;

const getClient = (envVars: PusherEnvVars): Pusher => {
  const key = `${envVars.REACT_APP_PUSHER_APP_KEY}:${envVars.REACT_APP_PUSHER_CLUSTER}`;
  if (client && clientKey !== key) {
    client.disconnect();
    client = null;
    channelRefCounts.clear();
  }
  if (!client) {
    client = new Pusher(envVars.REACT_APP_PUSHER_APP_KEY, {
      cluster: envVars.REACT_APP_PUSHER_CLUSTER,
    });
    clientKey = key;
  }
  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }
  return client;
};

const scheduleDisconnect = () => {
  if (disconnectTimer) clearTimeout(disconnectTimer);
  disconnectTimer = setTimeout(() => {
    disconnectTimer = null;
    if (client && channelRefCounts.size === 0) {
      client.disconnect();
      client = null;
    }
  }, DISCONNECT_GRACE_MS);
};

/**
 * Subscribes to a channel and binds the given handlers. Returns a release
 * function that unbinds only these handlers and drops the channel and the
 * socket when nobody else holds them.
 */
export const subscribeToPusherChannel = (
  envVars: PusherEnvVars,
  channelName: string,
  handlers: PusherEventHandlers,
): (() => void) => {
  if (!envVars.REACT_APP_PUSHER_APP_KEY || !channelName) return () => {};

  const pusher = getClient(envVars);
  const channel = pusher.subscribe(channelName);
  channelRefCounts.set(
    channelName,
    (channelRefCounts.get(channelName) ?? 0) + 1,
  );

  Object.entries(handlers).forEach(([event, handler]) => {
    channel.bind(event, handler);
  });

  let released = false;
  return () => {
    if (released) return;
    released = true;

    Object.entries(handlers).forEach(([event, handler]) => {
      channel.unbind(event, handler);
    });

    const remaining = (channelRefCounts.get(channelName) ?? 1) - 1;
    if (remaining > 0) {
      channelRefCounts.set(channelName, remaining);
      return;
    }
    channelRefCounts.delete(channelName);
    if (pusher === client) pusher.unsubscribe(channelName);
    if (channelRefCounts.size === 0) scheduleDisconnect();
  };
};

/** Test seam: drops the shared socket and every subscription. */
export const resetPusherClientForTests = () => {
  if (disconnectTimer) clearTimeout(disconnectTimer);
  disconnectTimer = null;
  if (client) client.disconnect();
  client = null;
  clientKey = '';
  channelRefCounts.clear();
};
